// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import {
  Alert,
  Box,
  Button,
  Radio,
  Theme,
  Typography
} from "@outschool/backpack";
import {
  MINIMUM_REFUND_CENTS,
  OSSubscriptions,
  parseDollarStringToCents
} from "@outschool/business-rules";
import {
  AccountSectionNewQuery,
  AccountSectionNewQueryVariables,
  EnrollmentAdminRow_PaymentFragment,
  EnrollmentAdminRow_SectionFragment,
  RefundEnrollmentCreditsMutation,
  RefundEnrollmentCreditsMutationVariables,
  RefundEnrollmentMutation,
  RefundEnrollmentMutationVariables,
  RefundType
} from "@outschool/gql-frontend-generated";
import {
  centsToNumericDollarString,
  renderCurrencyString
} from "@outschool/localization";
import { useOrderRefunded } from "@outschool/ui-analytics";
import { useLazyQuery, useMutation } from "@outschool/ui-apollo";
import {
  ConfirmationModal,
  Input,
  Select,
  Space
} from "@outschool/ui-legacy-component-library";
import React from "react";

import * as Enrollment from "../../shared/Enrollment";
import * as EnrollmentPayment from "../../shared/EnrollmentPayment";
import * as EnrollmentPaymentRefund from "../../shared/EnrollmentPaymentRefund";
import * as Env from "../../shared/Env";
import { AccountSectionQuery } from "../queries/AccountSectionQuery";
import {
  refundEnrollmentPaymentCreditsMutation,
  refundEnrollmentPaymentMutation
} from "../queries/EnrollmentPaymentMutations";
import ErrorMessage from "./ErrorMessage";

enum FundingSources {
  OutschoolFunds = "OUTSCHOOL_FUNDS",
  TeacherPayout = "TEACHER_PAYOUT"
}

enum ServiceFeeRefundState {
  RemainingRefundableAmount = "REMAINING_REFUNDABLE_AMOUNT",
  AlreadyRefunded = "ALREADY_REFUNDED",
  EligbleForRefund = "ELIGIBLE_FOR_REFUND"
}

interface EnrollmentPaymentServiceFee {
  uid: string;
  order_slug_id: string | null;
  amount_cents: number;
  refundState: ServiceFeeRefundState;
  start_time: any | null;
}

function refundToName({ isAdmin, enrollmentPayment, enrollment }: $TSFixMe) {
  return `${
    enrollmentPayment.organization
      ? isAdmin
        ? enrollmentPayment.organization.name
        : "learner's school"
      : enrollment.parent?.name ||
        enrollment.parent?.email ||
        enrollment.buyer?.name ||
        enrollment.buyer?.email
  } (for ${
    enrollment?.learner
      ? Enrollment.displayName(enrollment)
      : Enrollment.unclaimedGiftDisplayName(enrollment)
  })`;
}

function FundingSourceSelector({
  setFundingSource,
  fundingSource,
  enrollmentPayment,
  refundReason,
  setRefundReason,
  setError,
  refundNote,
  setRefundNote,
  error,
  mutationInProgress,
  refundType,
  setRefundType
}: {
  enrollmentPayment: EnrollmentAdminRow_PaymentFragment;
  refundReason: string;
  setRefundReason(rr: string): void;
  refundNote: string;
  setRefundNote(rr: string): void;
  error: string | null;
  setError(err: string | null): void;
  fundingSource: FundingSources | null;
  setFundingSource(fs: FundingSources | null): void;
  mutationInProgress: boolean;
  refundType: RefundType | null;
  setRefundType(rt: RefundType | null): void;
}) {
  const isPublicFundingPayment =
    EnrollmentPayment.isCharterOrder(enrollmentPayment) ||
    EnrollmentPayment.isESAOrder(enrollmentPayment);
  const isInternationalPayment =
    EnrollmentPayment.isInternationalPayment(enrollmentPayment);

  const handleUpdateRefundType = (newType: RefundType) => {
    if (newType === RefundType.AddOutschoolCredit) {
      setFundingSource(FundingSources.OutschoolFunds);
    }
    setRefundType(newType);
  };

  return (
    <Box sx={{ minWidth: "190px", maxWidth: "400px", paddingTop: "1em" }}>
      <Box>
        <Box>
          Refund type
          {(isPublicFundingPayment || isInternationalPayment) && (
            <Typography
              sx={(theme: Theme) => ({
                color: theme.palette.error.main,
                marginLeft: ".25em"
              })}
            >
              ({isPublicFundingPayment ? "Public Funding" : "International"}{" "}
              payments must use the Standard Refund type)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", height: "42px" }}>
          <Radio
            disabled={isPublicFundingPayment || isInternationalPayment}
            value={RefundType.AddOutschoolCredit}
            checked={refundType === RefundType.AddOutschoolCredit}
            onChange={() => {
              handleUpdateRefundType(RefundType.AddOutschoolCredit);
            }}
            sx={{ marginRight: ".5em", paddingX: "0em" }}
          />
          <Typography as={Box}>Add Outschool Credit</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", height: "42px" }}>
          <Radio
            disabled={isPublicFundingPayment || isInternationalPayment}
            value={RefundType.StandardRefund}
            checked={refundType === RefundType.StandardRefund}
            onChange={() => {
              handleUpdateRefundType(RefundType.StandardRefund);
            }}
            sx={{ marginRight: ".5em", paddingX: "0em" }}
          />
          <Typography as={Box}>Standard Refund</Typography>
        </Box>
      </Box>
      <Box sx={{ marginTop: "1em" }}>
        <Box>
          Refund from
          {refundType === RefundType.AddOutschoolCredit && (
            <Typography
              sx={(theme: Theme) => ({
                color: theme.palette.error.main,
                marginLeft: ".25em"
              })}
            >
              (Add Outschool Credit refunds cannot refund from teacher payout)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", height: "42px" }}>
          <Radio
            disabled={refundType === RefundType.AddOutschoolCredit}
            name="fundSource"
            value="outschool"
            id={`fundSourceOutschool${enrollmentPayment.uid}`}
            checked={fundingSource === FundingSources.OutschoolFunds}
            onChange={() => {
              setFundingSource(FundingSources.OutschoolFunds);
            }}
            sx={{ marginRight: ".5em", paddingX: "0em" }}
          />
          <Typography
            as={Box}
            htmlFor={`fundSourceOutschool${enrollmentPayment.uid}`}
          >
            Outschool funds
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", height: "42px" }}>
          <Radio
            disabled={refundType === RefundType.AddOutschoolCredit}
            name="fundSource"
            value="teacher"
            id={`fundSourceTeacher${enrollmentPayment.uid}`}
            checked={fundingSource === FundingSources.TeacherPayout}
            onChange={() => {
              setFundingSource(FundingSources.TeacherPayout);
            }}
            sx={{ marginRight: ".5em", paddingX: "0em" }}
          />
          <Typography
            as={Box}
            htmlFor={`fundSourceTeacher${enrollmentPayment.uid}`}
          >
            teacher payout
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: "1em",
          marginRight: "0.5em",
          flexGrow: 1
        }}
      >
        <Select
          name="refundReason"
          options={
            refundType === RefundType.AddOutschoolCredit
              ? EnrollmentPaymentRefund.ADMIN_REFUND_ADD_OUTSCHOOL_CREDIT_REASON_OPTIONS
              : fundingSource === FundingSources.TeacherPayout
              ? EnrollmentPaymentRefund.ADMIN_REFUND_STANDARD_REFUND_FROM_TEACHER_PAYOUT_OPTIONS
              : EnrollmentPaymentRefund.ADMIN_REFUND_REASON_OPTIONS
          }
          value={refundReason}
          placeholder="Choose a reason..."
          onChange={(value: string) => {
            setRefundReason(value);
            setError(null);
          }}
          sx={{
            width: "100%",
            boxSizing: "border-box"
          }}
        />
        <textarea
          value={refundNote}
          placeholder="Refund note (will be shared with parent)"
          onChange={e => {
            setRefundNote(e.target.value);
            setError(null);
          }}
          rows={2}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: "1em",
            padding: "0.25em"
          }}
        />
      </Box>
      <Button
        type="submit"
        disabled={!!error || mutationInProgress}
        sx={{
          marginTop: "1em"
        }}
      >
        {mutationInProgress ? "Refunding..." : "Refund..."}
      </Button>
    </Box>
  );
}

type EnrollmentPaymentRefundFormProps = {
  section: EnrollmentAdminRow_SectionFragment;
  enrollment:
    | Enrollment.EnrollmentWithPayments
    | NonNullable<
        NonNullable<AccountSectionNewQuery["section"]>["selectedEnrollment"]
      >;
  enrollmentPayment:
    | EnrollmentAdminRow_PaymentFragment
    | NonNullable<
        NonNullable<
          AccountSectionNewQuery["section"]
        >["learnerEnrollmentPayments"]
      >[number];
  isAdmin: boolean;
  isServiceFeeRefund?: boolean | null;
};

export default function EnrollmentPaymentRefundForm({
  isAdmin,
  enrollmentPayment,
  enrollment,
  section,
  isServiceFeeRefund
}: EnrollmentPaymentRefundFormProps) {
  const isPublicFundingPayment =
    EnrollmentPayment.isCharterOrder(enrollmentPayment) ||
    EnrollmentPayment.isESAOrder(enrollmentPayment);
  const isInternationalPayment =
    EnrollmentPayment.isInternationalPayment(enrollmentPayment);
  const isSubscriptionCreditPayment =
    (enrollmentPayment as EnrollmentAdminRow_PaymentFragment)
      ?.credit_account_type === "subscription" ||
    (enrollmentPayment as EnrollmentAdminRow_PaymentFragment)
      ?.credit_account_type === "esa_membership";
  const [fundingSource, setFundingSource] =
    React.useState<FundingSources | null>(
      isAdmin ? FundingSources.OutschoolFunds : FundingSources.TeacherPayout
    );
  const [refundType, setRefundType] = React.useState<RefundType | null>(
    !isAdmin || isPublicFundingPayment || isInternationalPayment
      ? RefundType.StandardRefund
      : RefundType.AddOutschoolCredit
  );
  const [showConfirmation, setShowConfirmation] =
    React.useState<boolean>(false);
  const [mutationInProgress, setMutationInProgress] =
    React.useState<boolean>(false);
  const [refundReason, setRefundReason] = React.useState("");
  const [refundNote, setRefundNote] = React.useState("");
  const [refundAmount, setRefundAmount] = React.useState(
    !enrollmentPayment.unrefundedCredits
      ? centsToNumericDollarString(enrollmentPayment.unrefundedCents)
      : ""
  );
  const [refundCreditAmount, setRefundCreditAmount] = React.useState(
    !!enrollmentPayment.unrefundedCredits
      ? enrollmentPayment.unrefundedCredits.toLocaleString()
      : ""
  );
  const [error, setError] = React.useState<string | null>(null);

  const trackRefund = useOrderRefunded();
  const [refundEnrollmentPayment] = useMutation<
    RefundEnrollmentMutation,
    RefundEnrollmentMutationVariables
  >(refundEnrollmentPaymentMutation);
  const [refundEnrollmentPaymentCredits] = useMutation<
    RefundEnrollmentCreditsMutation,
    RefundEnrollmentCreditsMutationVariables
  >(refundEnrollmentPaymentCreditsMutation);
  const [refetchAccountSectionQuery] = useLazyQuery<
    AccountSectionNewQuery,
    AccountSectionNewQueryVariables
  >(AccountSectionQuery);

  // When inputs are valid, returns true.
  // When inputs are not valid, returns false and sets this.state.error
  const validate = () => {
    if (
      !!enrollmentPayment.credit_transaction_uid &&
      (enrollmentPayment.credit_account_type === "subscription" ||
        enrollmentPayment.credit_account_type === "esa_membership")
    ) {
      return validateCredits();
    }

    return validateCents();
  };

  const validateCents = () => {
    const refundAmountCents = parseDollarStringToCents(refundAmount);
    if (!refundAmount || isNaN(refundAmountCents)) {
      setError("invalid amount");
      return false;
    }

    const isCreditServiceFeeRefund =
      isServiceFeeRefund && refundType === RefundType.AddOutschoolCredit;
    const unrefundedCents = enrollmentPayment.unrefundedCents;

    const refundTooLarge = refundAmountCents > unrefundedCents;
    const refundTooSmall =
      refundAmountCents < MINIMUM_REFUND_CENTS && !isCreditServiceFeeRefund;
    const refundTooClose =
      refundAmountCents > unrefundedCents - MINIMUM_REFUND_CENTS &&
      refundAmountCents != unrefundedCents;

    const minRefundString = renderCurrencyString({
      priceInCents: MINIMUM_REFUND_CENTS
    });

    if (refundTooLarge) {
      const error = `You can refund at most ${renderCurrencyString({
        priceInCents: unrefundedCents
      })} for this payment`;
      setError(error);
      return false;
    } else if (refundTooSmall) {
      const error = `The minimum refund amount is ${minRefundString}.`;
      setError(error);
      return false;
    } else if (refundTooClose) {
      const error = `The minimum refund amount is ${minRefundString}. You cannot leave less than ${minRefundString} unrefunded.`;
      setError(error);
      return false;
    }

    if (isAdmin || showConfirmation) {
      if (!fundingSource) {
        setError("Please select a funding source");
        return false;
      }
      if (!refundReason) {
        setError("Please choose a reason");
        return false;
      }
      if (!refundNote) {
        setError("Please add a note with more details");
        return false;
      }
    }
    return true;
  };

  const validateCredits = () => {
    const refundAmountCredits = parseInt(refundCreditAmount);
    const creditToString = refundAmountCredits.toString();
    if (
      !refundCreditAmount ||
      isNaN(refundAmountCredits) ||
      creditToString !== refundCreditAmount
    ) {
      setError("invalid amount");
      return false;
    }

    const unrefundedCredits = enrollmentPayment.unrefundedCredits!;
    const refundTooLarge = refundAmountCredits > unrefundedCredits;
    if (refundTooLarge) {
      const error = `You can refund at most ${unrefundedCredits} credits for this payment`;
      setError(error);
      return false;
    } else if (refundAmountCredits <= 0) {
      const error = `The minimum refund amount is 1 credit.`;
      setError(error);
      return false;
    }

    if (isAdmin || showConfirmation) {
      if (!fundingSource) {
        setError("Please select a funding source");
        return false;
      }
      if (!refundReason) {
        setError("Please choose a reason");
        return false;
      }
      if (!refundNote) {
        setError("Please add a note with more details");
        return false;
      }
    }
    return true;
  };

  const handleServiceFeeRefund = (serviceFee: number) => {
    setFundingSource(FundingSources.OutschoolFunds);
    setRefundNote("Refunding service fee for full refund");
    setRefundAmount(centsToNumericDollarString(serviceFee));
    // If the service fee is less than the minimum refund amount, we will issue
    // an Outschool credit refund instead of a standard refund to workaround the
    // Stripe minimum refund amount.
    setRefundType(
      serviceFee < MINIMUM_REFUND_CENTS
        ? RefundType.AddOutschoolCredit
        : RefundType.StandardRefund
    );
    setRefundReason("service fee refund");
    setShowConfirmation(true);
  };

  const handleSubmit = (e: $TSFixMe) => {
    e && e.preventDefault();
    if (!validate()) {
      return;
    }
    setShowConfirmation(true);
  };

  const refund = async () => {
    if (!validate()) {
      return false;
    }
    let refundAmountCents = 0;
    setMutationInProgress(true);
    try {
      if (!enrollmentPayment.unrefundedCredits) {
        refundAmountCents = parseDollarStringToCents(refundAmount);

        await refundEnrollmentPayment({
          variables: {
            enrollmentPaymentUid: enrollmentPayment.uid,
            refundAmountCents,
            refundReason,
            refundNote,
            refundType,
            reducesTeacherPayout: fundingSource === FundingSources.TeacherPayout
          }
        });
      } else {
        const refundAmountCredits = parseInt(refundCreditAmount);

        refundAmountCents =
          OSSubscriptions.creditsToPriceCents(refundAmountCredits);
        await refundEnrollmentPaymentCredits({
          variables: {
            enrollmentPaymentUid: enrollmentPayment.uid,
            refundAmountCredits,
            refundReason,
            refundNote,
            refundType,
            reducesTeacherPayout: fundingSource === FundingSources.TeacherPayout
          }
        });
      }

      try {
        const isUnclaimedGift =
          enrollment.__typename === "GiftEnrollment" &&
          !Boolean(enrollment.giftClaimedAt);
        const learnerOrEnrollmentUid = isUnclaimedGift
          ? enrollment.uid
          : enrollment.learner?.uid;
        await refetchAccountSectionQuery({
          variables: {
            uid: section.uid,
            // The only other code that makes this query uses the empty
            // string fallback, so we must do that here.
            learnerOrEnrollmentUid: learnerOrEnrollmentUid || "",
            hasSelection: Boolean(learnerOrEnrollmentUid)
          }
        });
      } catch (err) {
        console.log("Couldn't refetch");
        OsPlatform.captureError(err);
      }

      try {
        const leader = section.leader;
        const activity = section.activity;
        const orderId = enrollmentPayment.order_slug_id;
        const currency = enrollmentPayment.currency_code;

        trackRefund(orderId!, refundAmountCents, currency!, {
          activity,
          leader
        });
      } catch (err) {
        console.log("Couldn't track refund");
        OsPlatform.captureError(err);
      }
    } catch (error) {
      setMutationInProgress(false);
      setError(error);
    } finally {
      setMutationInProgress(false);
    }
    return true;
  };

  if (!enrollment.confirmedAt) {
    return null;
  }

  return (
    <div>
      <ErrorMessage
        value={!showConfirmation && error}
        showStatusPageLink={Env.IS_READ_ONLY_MODE}
      />
      {isServiceFeeRefund && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            flexGrow: 1,
            maxWidth: "20em"
          }}
        >
          <Button
            disabled={
              mutationInProgress ||
              (enrollmentPayment as unknown as EnrollmentPaymentServiceFee)
                .refundState !== ServiceFeeRefundState.EligbleForRefund
            }
            sx={{
              flexGrow: 1
            }}
            onClick={() => {
              handleServiceFeeRefund(enrollmentPayment.amount_cents || 0);
            }}
          >
            {mutationInProgress
              ? "Refunding service fee..."
              : "Refund service fee..."}
          </Button>
        </Box>
      )}
      {!isServiceFeeRefund && !isSubscriptionCreditPayment && (
        <form onSubmit={handleSubmit}>
          <Box sx={{ marginY: "1em" }}>
            <Box
              flex
              sx={{
                flexDirection: "column",
                flexGrow: 1,
                maxWidth: "400px"
              }}
            >
              <label
                htmlFor="enrollment-payment-refund-amount"
                style={{
                  whiteSpace: "nowrap"
                }}
              >
                Refund amount USD
              </label>
              <Input
                id="enrollment-payment-refund-amount"
                name="enrollment-payment-refund-amount"
                type="text"
                value={refundAmount}
                onChange={e => {
                  setRefundAmount(e.target.value);
                  setError(null);
                }}
                sx={{
                  marginRight: "0.5em",
                  height: "44px",
                  borderColor: "neutral.400"
                }}
              />
            </Box>
            {isAdmin ? (
              <FundingSourceSelector
                enrollmentPayment={enrollmentPayment}
                mutationInProgress={mutationInProgress}
                setFundingSource={setFundingSource}
                fundingSource={fundingSource}
                refundType={refundType}
                setRefundType={setRefundType}
                refundReason={refundReason}
                setRefundReason={setRefundReason}
                refundNote={refundNote}
                setRefundNote={setRefundNote}
                error={error}
                setError={setError}
              />
            ) : (
              <Button
                type="submit"
                disabled={!!error || mutationInProgress}
                sx={{
                  flexGrow: 1
                }}
              >
                {mutationInProgress ? "Refunding..." : "Refund..."}
              </Button>
            )}
          </Box>
        </form>
      )}
      {!isServiceFeeRefund && isSubscriptionCreditPayment && (
        <form onSubmit={handleSubmit}>
          <Box sx={{ marginY: "1em" }}>
            <Box
              flex
              sx={{
                flexDirection: "column",
                flexGrow: 1,
                maxWidth: "400px"
              }}
            >
              <label
                htmlFor="enrollment-payment-refund-amount"
                style={{
                  whiteSpace: "nowrap"
                }}
              >
                Refund amount membership credit
              </label>
              <Input
                id="enrollment-payment-refund-amount"
                name="enrollment-payment-refund-amount"
                type="text"
                value={refundCreditAmount || 0}
                onChange={e => {
                  setRefundCreditAmount(e.target.value);
                  setError(null);
                }}
                sx={{
                  marginRight: "0.5em",
                  height: "44px",
                  borderColor: "neutral.400"
                }}
              />
            </Box>
            {isAdmin ? (
              <FundingSourceSelector
                enrollmentPayment={enrollmentPayment}
                mutationInProgress={mutationInProgress}
                setFundingSource={setFundingSource}
                fundingSource={fundingSource}
                refundType={refundType}
                setRefundType={setRefundType}
                refundReason={refundReason}
                setRefundReason={setRefundReason}
                refundNote={refundNote}
                setRefundNote={setRefundNote}
                error={error}
                setError={setError}
              />
            ) : (
              <Button
                type="submit"
                disabled={!!error || mutationInProgress}
                sx={{
                  flexGrow: 1,
                  marginTop: "0.5em"
                }}
              >
                {mutationInProgress ? "Refunding..." : "Refund..."}
              </Button>
            )}
          </Box>
        </form>
      )}
      <ConfirmationModal
        buttonColor="danger"
        open={showConfirmation}
        disabled={mutationInProgress}
        headline={
          isAdmin
            ? "Please confirm that this is accurate before submitting."
            : `Refund to ${refundToName({
                isAdmin,
                enrollment,
                enrollmentPayment
              })}`
        }
        content={
          isAdmin ? (
            <Box>
              <Space y="medium" />
              {isServiceFeeRefund &&
                refundType === RefundType.AddOutschoolCredit &&
                enrollmentPayment?.amount_cents &&
                enrollmentPayment.amount_cents < MINIMUM_REFUND_CENTS && (
                  <Alert severity="warning">
                    This will issue a credit refund instead of a standard refund
                    since the service fee is less than the minimum stripe refund
                    amount.
                  </Alert>
                )}
              <Box>
                You are about to process a{" "}
                {refundType === RefundType.AddOutschoolCredit
                  ? "credit:"
                  : "refund:"}{" "}
              </Box>
              <Space y="small" />
              <Box>
                <b>Amount: </b>
                {!!refundCreditAmount
                  ? `${refundCreditAmount} credits`
                  : refundAmount}
              </Box>
              <Space y="small" />
              <Box>
                <b>From funding source: </b>
                {fundingSource}
              </Box>
              <Space y="small" />
              <Box>
                <b>To Outschool user: </b>
                {refundToName({
                  isAdmin,
                  enrollment,
                  enrollmentPayment
                })}
              </Box>
              <Space y="small" />
              <Box>
                <b>For reason: </b>
                {refundReason}
              </Box>
              <Space y="small" />
              <Box>
                <b>With additional note: </b>
                {refundNote}
              </Box>
            </Box>
          ) : (
            <div>
              <ErrorMessage
                value={error}
                showStatusPageLink={Env.IS_READ_ONLY_MODE}
              />
              <Box
                sx={{
                  paddingY: "0.5em"
                }}
              >
                Why are you issuing this refund?
              </Box>
              <div>
                <Select
                  name="refundReason"
                  options={
                    EnrollmentPaymentRefund.TEACHER_REFUND_REASON_OPTIONS
                  }
                  value={refundReason}
                  placeholder="Choose a reason..."
                  onChange={(value: string) => {
                    setRefundReason(value);
                    setError(null);
                  }}
                  sx={{
                    marginBottom: "0.5em"
                  }}
                ></Select>
              </div>
              <Box
                sx={{
                  paddingY: "0.5em"
                }}
              >
                Please tell Outschool more about this refund:
              </Box>
              <textarea
                value={refundNote}
                placeholder={`Note for Outschool and ${enrollment.parent?.name}`}
                onChange={e => {
                  setRefundNote(e.target.value);
                  setError(null);
                }}
                rows={2}
                style={{
                  marginBottom: "0.5em",
                  width: "100%",
                  boxSizing: "border-box",
                  height: "auto"
                }}
              ></textarea>
            </div>
          )
        }
        confirmationLabel={
          mutationInProgress
            ? `${
                refundType === RefundType.AddOutschoolCredit
                  ? "Crediting..."
                  : "Refunding..."
              }`
            : `${
                refundType === RefundType.AddOutschoolCredit
                  ? "Credit"
                  : "Refund"
              }
              ${
                !!refundCreditAmount
                  ? `${refundCreditAmount} credits`
                  : `$${refundAmount}`
              }
              ${
                isAdmin
                  ? ` from ${
                      fundingSource === FundingSources.TeacherPayout
                        ? "teacher payout"
                        : fundingSource === FundingSources.OutschoolFunds
                        ? "Outschool funds"
                        : ""
                    }`
                  : ""
              }`
        }
        onConfirm={async () => {
          if (await refund()) {
            setShowConfirmation(false);
            setError(null);
          }
        }}
        onClose={() => {
          setShowConfirmation(false);
          setError(null);
        }}
      />
    </div>
  );
}
